import { ORPCError } from "@orpc/client";
import {
  transcriptConflictSchema,
  visitTranscriptSchema,
} from "@rosc/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { referenceClient } from "@/lib/orpc/client";
import { transcriptVersion } from "@/lib/transcript-edit";
import { refreshVisitReads, useVisitScope } from "@/lib/visit-queries";

export type TranscriptSaveOutcome =
  | "saved"
  | "unchanged"
  | "stale"
  | "busy"
  | "error";

// Saves the clinician's corrected transcript of the visit's first recording.
// `base` is the transcript the edit started from; the API refuses the write
// if the stored transcript changed since.
export function useTranscriptEdit(visitPublicId: string) {
  const scope = useVisitScope();
  const [saving, setSaving] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const save = useCallback(
    async (base: string, next: string): Promise<TranscriptSaveOutcome> => {
      if (!scope || !visitPublicId) return "error";
      setSaving(true);
      try {
        const result = visitTranscriptSchema.parse(
          await referenceClient.updateVisitTranscript(
            {
              params: { public_id: visitPublicId },
              query: {},
              body: {
                transcription: next,
                baseVersion: await transcriptVersion(base),
              },
            },
            { context: { expectedUserUid: scope.uid } },
          ),
        );
        await refreshVisitReads(scope);
        return result.changed ? "saved" : "unchanged";
      } catch (error) {
        if (error instanceof ORPCError && error.code === "CONFLICT") {
          const conflict = transcriptConflictSchema.safeParse(error.data);
          void refreshVisitReads(scope);
          return conflict.success && conflict.data.reason === "busy"
            ? "busy"
            : "stale";
        }
        return "error";
      } finally {
        if (mounted.current) setSaving(false);
      }
    },
    [scope, visitPublicId],
  );
  return { save, saving };
}
